import { Building, ChevronDown, ExternalLink, Eye, Globe, Lock, Shield, Users } from 'lucide-react'
import { useState } from 'react'
import { SiGoogle } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleSearchConsoleListSites,
  type GoogleSearchConsoleListSitesResult,
} from 'sprites-types/workflow/tools/googleSearchConsoleListSites'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function SiteItem({
  site,
  className,
}: {
  site: { siteUrl: string; permissionLevel: string }
  className?: string
}) {
  const { siteUrl, permissionLevel } = site

  const getPermissionStyles = (permission: string) => {
    const permissionStyles: Record<string, any> = {
      siteOwner: {
        bg: 'bg-gradient-to-br from-green-50 to-green-100/50',
        border: 'border-green-200',
        shadow: 'shadow-md shadow-green-100/50',
        text: 'text-green-700',
        badge: 'bg-green-100 text-green-700 border-green-200',
        icon: Building,
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
      },
      siteFullUser: {
        bg: 'bg-gradient-to-br from-blue-50 to-blue-100/50',
        border: 'border-blue-200',
        shadow: 'shadow-md shadow-blue-100/50',
        text: 'text-blue-700',
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: Users,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
      },
      siteRestrictedUser: {
        bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100/50',
        border: 'border-yellow-200',
        shadow: 'shadow-md shadow-yellow-100/50',
        text: 'text-yellow-700',
        badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        icon: Lock,
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
      },
      siteUnverifiedUser: {
        bg: 'bg-gradient-to-br from-red-50 to-red-100/50',
        border: 'border-red-200',
        shadow: 'shadow-md shadow-red-100/50',
        text: 'text-red-700',
        badge: 'bg-red-100 text-red-700 border-red-200',
        icon: Eye,
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
      },
    }
    return (
      permissionStyles[permission] || {
        bg: 'bg-gradient-to-br from-gray-50 to-gray-100/50',
        border: 'border-gray-200',
        shadow: 'shadow-md shadow-gray-100/50',
        text: 'text-gray-700',
        badge: 'bg-gray-100 text-gray-700 border-gray-200',
        icon: Shield,
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
      }
    )
  }

  const formatPermissionLevel = (permission: string): string => {
    const permissionLabels: Record<string, string> = {
      siteOwner: 'Owner',
      siteFullUser: 'Full User',
      siteRestrictedUser: 'Restricted User',
      siteUnverifiedUser: 'Unverified User',
    }
    return permissionLabels[permission] || permission
  }

  const formatSiteUrl = (url: string): string => {
    if (url.startsWith('sc-domain:')) {
      return url.replace('sc-domain:', '')
    }
    return url
  }

  const getSiteType = (url: string): string => {
    if (url.startsWith('sc-domain:')) {
      return 'Domain Property'
    }
    return 'URL Prefix Property'
  }

  const getViewUrl = (url: string): string => {
    if (url.startsWith('sc-domain:')) {
      const domain = url.replace('sc-domain:', '')
      return `https://${domain}`
    }
    return url
  }

  const permissionStyles = getPermissionStyles(permissionLevel)
  const PermissionIcon = permissionStyles.icon

  return (
    <div
      className={`overflow-hidden rounded-2xl border-2 ${permissionStyles.border} ${permissionStyles.bg} ${permissionStyles.shadow} transition-all duration-200 hover:shadow-lg ${className}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Site Type Icon */}
          <div className="flex-shrink-0">
            <div className="rounded-lg bg-[#4285f4]/10 p-2">
              <Globe className="h-5 w-5 text-[#4285f4]" />
            </div>
          </div>

          {/* Site Details */}
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="mb-1 text-lg font-bold text-gray-900">{formatSiteUrl(siteUrl)}</div>
                <div className="mb-2 text-sm text-gray-600">{getSiteType(siteUrl)}</div>
              </div>

              {/* Permission Badge */}
              <div
                className={`flex-shrink-0 rounded-full border px-3 py-1.5 ${permissionStyles.badge}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`rounded-lg p-1 ${permissionStyles.iconBg}`}>
                    <PermissionIcon className={`h-3 w-3 ${permissionStyles.iconColor}`} />
                  </div>
                  <span className="text-xs font-semibold">
                    {formatPermissionLevel(permissionLevel)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-gray-500">
                {permissionLevel === 'siteOwner'
                  ? 'Full administrative access'
                  : permissionLevel === 'siteFullUser'
                    ? 'View and manage data'
                    : permissionLevel === 'siteRestrictedUser'
                      ? 'Limited access permissions'
                      : 'View-only access'}
              </div>
              <a
                href={getViewUrl(siteUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white/80 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-white hover:shadow-sm"
              >
                <ExternalLink className="h-3 w-3" />
                View Site
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function GoogleSearchConsoleListSitesDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleSearchConsoleListSites, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleSearchConsoleListSitesResult

  const sites = result.sites || []

  if (sites.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <SiGoogle className="h-5 w-5 text-[#4285f4]" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">Google Search Console Sites</div>
            <div className="text-xs text-gray-600">No sites found in your account</div>
          </div>
        </div>
      </div>
    )
  }

  if (sites.length === 1) {
    return <SiteItem site={sites[0]} />
  }

  // Group sites by permission level for better organization
  const sitesByPermission = sites.reduce(
    (acc, site) => {
      if (!acc[site.permissionLevel]) {
        acc[site.permissionLevel] = []
      }
      acc[site.permissionLevel].push(site)
      return acc
    },
    {} as Record<string, typeof sites>
  )

  const owners = sitesByPermission.siteOwner || []
  const fullUsers = sitesByPermission.siteFullUser || []
  const restrictedUsers = sitesByPermission.siteRestrictedUser || []
  const unverifiedUsers = sitesByPermission.siteUnverifiedUser || []

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <SiGoogle className="h-5 w-5 text-[#4285f4]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">Google Search Console Sites</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {sites.length} site{sites.length !== 1 ? 's' : ''} • {owners.length} owned
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-600">
                {sites.length} Sites
              </div>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>

          {isExpanded && (
            <div className="border-t border-gray-200/50 p-4">
              <div className="space-y-6">
                {/* Owner Sites */}
                {owners.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <Building className="h-4 w-4 text-green-600" />
                      <div className="text-sm font-bold text-gray-900">Owned Sites</div>
                      <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                        {owners.length}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {owners.map((site, index) => (
                        <SiteItem key={site.siteUrl || `owner-${index}`} site={site} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Full User Sites */}
                {fullUsers.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      <div className="text-sm font-bold text-gray-900">Full Access Sites</div>
                      <div className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        {fullUsers.length}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {fullUsers.map((site, index) => (
                        <SiteItem key={site.siteUrl || `full-${index}`} site={site} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Restricted User Sites */}
                {restrictedUsers.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <Lock className="h-4 w-4 text-yellow-600" />
                      <div className="text-sm font-bold text-gray-900">Restricted Access Sites</div>
                      <div className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-700">
                        {restrictedUsers.length}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {restrictedUsers.map((site, index) => (
                        <SiteItem key={site.siteUrl || `restricted-${index}`} site={site} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Unverified User Sites */}
                {unverifiedUsers.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center gap-2">
                      <Eye className="h-4 w-4 text-red-600" />
                      <div className="text-sm font-bold text-gray-900">View-Only Sites</div>
                      <div className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                        {unverifiedUsers.length}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {unverifiedUsers.map((site, index) => (
                        <SiteItem key={site.siteUrl || `unverified-${index}`} site={site} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ContextMenuWrapper>
    </div>
  )
}

function downloadGoogleSearchConsoleListSites(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleSearchConsoleListSitesToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleSearchConsoleListSites, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleSearchConsoleListSitesResult
  const sites = result.sites || []

  if (sites.length === 0) {
    return `## Google Search Console Sites

No sites found.

---
`
  }

  const formatSiteUrl = (url: string): string => {
    if (url.startsWith('sc-domain:')) {
      return url.replace('sc-domain:', '')
    }
    return url
  }

  const getSiteType = (url: string): string => {
    if (url.startsWith('sc-domain:')) {
      return 'Domain Property'
    }
    return 'URL Prefix Property'
  }

  const formatPermissionLevel = (permission: string): string => {
    const permissionLabels: Record<string, string> = {
      siteOwner: 'Owner',
      siteFullUser: 'Full User',
      siteRestrictedUser: 'Restricted User',
      siteUnverifiedUser: 'Unverified User',
    }
    return permissionLabels[permission] || permission
  }

  const sitesMarkdown = sites
    .map((site) => {
      return `### ${formatSiteUrl(site.siteUrl)}

**Type:** ${getSiteType(site.siteUrl)}
**Permission Level:** ${formatPermissionLevel(site.permissionLevel)}
**URL:** ${site.siteUrl}
`
    })
    .join('\n---\n\n')

  return `## Google Search Console Sites (${sites.length} ${sites.length === 1 ? 'site' : 'sites'})

${sitesMarkdown}

---
`
}

export const GoogleSearchConsoleListSitesDisplay = createWorkflowToolComponent(
  GoogleSearchConsoleListSitesDisplayComponent,
  downloadGoogleSearchConsoleListSites,
  googleSearchConsoleListSitesToMarkdown
)
